import { useEffect, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  type DocumentData,
  type Query,
  type QueryConstraint,
  query,
} from "firebase/firestore";
import { db } from "./firebase";

export function useCollection<T extends { id: string }>(path: string, ...constraints: QueryConstraint[]) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const source = constraints.length
      ? query(collection(db, path), ...constraints)
      : collection(db, path);
    return onSnapshot(source as Query<DocumentData>, (snapshot) => {
      // The Firestore document id is canonical. Some legacy documents contain
      // an empty or stale `id` field which must never override entry.id.
      setData(snapshot.docs.map((entry) => ({ ...entry.data(), id: entry.id } as T)));
      setLoading(false);
      setError(null);
    }, (reason) => {
      setError(reason.message);
      setLoading(false);
    });
    // Constraints are stable module-level values in current callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return { data, loading, error };
}

export function useDocument<T>(path: string, id: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => onSnapshot(doc(db, path, id), (snapshot) => {
    setData(snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as T) : null);
    setLoading(false);
  }, () => setLoading(false)), [path, id]);

  return { data, loading };
}
